import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query';

import {
  addRestaurantFromBORequest,
  AddRestaurantParams,
  AddRestaurantParamsFromBO,
  addRestaurantPicture,
  AddRestaurantPictureParams,
  addRestaurantRequest,
  AddRestaurantResponse,
  approveInactiveRestaurantRequest,
  checkRestaurantFromBOTokenRequest,
  CityStripeStatus,
  deleteRestaurantPictureById,
  deleteRestaurantRequest,
  DeleteRestaurantResponse,
  FetchAutocompletePlaceParams,
  fetchAutocompletePlaceRequest,
  FetchAutocompleteRestaurantParams,
  fetchAutocompleteRestaurantRequest,
  fetchAvailableNearbyCitiesRequest,
  fetchLastRestaurantsRequest,
  fetchPublicRestaurantRequest,
  FetchRestaurantOptions,
  fetchRestaurantPictureById,
  fetchRestaurantPictures,
  fetchRestaurantRequest,
  FetchRestaurantsByOwnerParams,
  fetchRestaurantsByOwnerRequest,
  fetchRestaurantsRequest,
  fetchSponsoredRestaurantsRequest,
  GetRestaurantPictureResponse,
  GetRestaurantPicturesResponse,
  getRestaurants,
  ISerializedPicture,
  PlaceResponse,
  rejectInactiveRestaurantRequest,
  RestaurantCheckTokenResponse,
  RestaurantPictureParams,
  RestaurantResponse,
  SimpleRestaurantResponse,
  updateRestaurant,
  updateRestaurantPictureById,
  UpdateRestaurantPictureParams,
  UpdateRestaurantResponse
} from '../lib/restaurant';
import { ErrorResponse, UseQueryConfig } from '../lib/types';
import { UpdateRestaurantSchemaType } from '@/components/organisms/RestaurantForm/schema';

export const useGetCompanies = () => {
  return useQuery(['restaurants'], () => getRestaurants());
};

export const useUpdateRestaurantMutation = ({
  onSuccess,
  onError
}: {
  onSuccess?: (response: UpdateRestaurantResponse) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateRestaurantResponse, ErrorResponse, UpdateRestaurantSchemaType>((params: UpdateRestaurantSchemaType) => updateRestaurant(params), {
    onSuccess: (response: UpdateRestaurantResponse, variables) => {
      queryClient.invalidateQueries(['restaurant', variables.restaurantId]);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (error: ErrorResponse) => {
      if (onError) {
        onError(error);
      } else {
        console.error('Failed to update restaurant:', error);
      }
    }
  });
};

export const useFetchSponsoredRestaurantsQuery = (config?: UseQueryConfig<RestaurantResponse[]>) => {
  return useQuery<RestaurantResponse[], ErrorResponse>({
    queryKey: ['sponsoredrestaurants'],
    queryFn: () => fetchSponsoredRestaurantsRequest(),
    ...config
  });
};

// PREFETCH
export const prefetchSponsoredRestaurantsQuery = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['sponsoredrestaurants'],
    queryFn: () => fetchSponsoredRestaurantsRequest()
  });
};

export const useFetchLastRestaurantsQuery = (config?: UseQueryConfig<RestaurantResponse[]>) => {
  return useQuery<RestaurantResponse[], ErrorResponse>({
    queryKey: ['lastrestaurants'],
    queryFn: () => fetchLastRestaurantsRequest(),
    ...config
  });
};

export const useFetchAutocompleteRestaurantMutation = ({
  onSuccess
}: {
  onSuccess?: (response: SimpleRestaurantResponse[], params: FetchAutocompleteRestaurantParams) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation<SimpleRestaurantResponse[], ErrorResponse, FetchAutocompleteRestaurantParams>((params) => fetchAutocompleteRestaurantRequest(params), {
    onSuccess: (data, params) => {
      queryClient.removeQueries();
      if (onSuccess) {
        onSuccess(data, params);
      }
    }
  });
};

// PREFETCH
export const prefetchLastRestaurantsQuery = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['lastrestaurants'],
    queryFn: () => fetchLastRestaurantsRequest()
  });
};

// verifier si sa fetch seulement les restos du owner
export const useFetchAutocompletePlaceMutation = ({ onSuccess }: { onSuccess?: (response: PlaceResponse[], params: FetchAutocompletePlaceParams) => void }) => {
  return useMutation<PlaceResponse[], ErrorResponse, FetchAutocompletePlaceParams>((params) => fetchAutocompletePlaceRequest(params), {
    onSuccess: (data, params) => {
      if (onSuccess) {
        onSuccess(data, params);
      }
    }
  });
};

export const useFetchBackofficeRestaurantsQuery = (config?: UseQueryConfig<SimpleRestaurantResponse[]>) => {
  return useQuery<SimpleRestaurantResponse[], ErrorResponse>({
    queryKey: ['restaurantsByEmail'],
    queryFn: () => fetchRestaurantsRequest(),
    ...config
  });
};

export const useFetchRestaurantQuery = ({ id, withMenu = false }: FetchRestaurantOptions, config?: UseQueryConfig<RestaurantResponse>) => {
  return useQuery<RestaurantResponse, ErrorResponse>({
    queryKey: ['restaurant', id, withMenu],
    queryFn: () => fetchRestaurantRequest(id, withMenu),
    ...config
  });
};

export const useFetchPublicRestaurantQuery = ({ id, withMenu = false }: FetchRestaurantOptions, config?: UseQueryConfig<RestaurantResponse>) => {
  return useQuery<RestaurantResponse, ErrorResponse>({
    queryKey: ['restaurant', id, withMenu],
    queryFn: () => fetchPublicRestaurantRequest(id, withMenu),
    ...config
  });
};

export const useDeleteRestaurantMutation = ({ onSuccess }: { onSuccess?: (response: DeleteRestaurantResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<DeleteRestaurantResponse, ErrorResponse, { restaurantId: string }>(({ restaurantId }) => deleteRestaurantRequest(restaurantId), {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['restaurant', variables.restaurantId]);
      queryClient.invalidateQueries(['restaurantsByEmail']);

      if (onSuccess) {
        onSuccess(data);
      }
    }
  });
};

export const useAddRestaurantMutation = ({ onSuccess }: { onSuccess?: (response: AddRestaurantResponse, params: AddRestaurantParams) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<AddRestaurantResponse, ErrorResponse, AddRestaurantParams>((params) => addRestaurantRequest(params), {
    onSuccess: (data, params) => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['restaurantsByEmail'] });
        onSuccess(data, params);
      }
    }
  });
};

export const useAddRestaurantFromBOMutation = ({ onSuccess }: { onSuccess?: (response: AddRestaurantResponse, params: AddRestaurantParamsFromBO) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<AddRestaurantResponse, ErrorResponse, AddRestaurantParamsFromBO>((params) => addRestaurantFromBORequest(params), {
    onSuccess: (data, params) => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['restaurants'] });
        onSuccess(data, params);
      }
    }
  });
};

export const useCheckAddRestaurantFromBOTokenQuery = (token: string, config?: UseQueryConfig<RestaurantCheckTokenResponse>) => {
  return useQuery<RestaurantCheckTokenResponse, ErrorResponse>({
    queryKey: ['restaurant'],
    queryFn: () => checkRestaurantFromBOTokenRequest(token),
    ...config
  });
};

export const useFetchRestaurantsByOwnerMutation = ({
  onSuccess
}: {
  onSuccess?: (response: SimpleRestaurantResponse[], params: FetchRestaurantsByOwnerParams) => void;
}) => {
  return useMutation<SimpleRestaurantResponse[], ErrorResponse, FetchRestaurantsByOwnerParams>((params) => fetchRestaurantsByOwnerRequest(params), {
    onSuccess: (data, params) => {
      if (onSuccess) {
        onSuccess(data, params);
      }
    }
  });
};

export const useFetchRestaurantPictureQuery = ({ restaurantId, pictureId }: RestaurantPictureParams, config?: UseQueryConfig<GetRestaurantPictureResponse>) => {
  return useQuery<GetRestaurantPictureResponse, ErrorResponse>({
    queryKey: ['restaurantPicture'],
    queryFn: () => fetchRestaurantPictureById({ restaurantId, pictureId }),
    ...config
  });
};

export const useFetchRestaurantPicturesQuery = (restaurantId: string, config?: UseQueryConfig<ISerializedPicture[]>) => {
  return useQuery<ISerializedPicture[], ErrorResponse>({
    queryKey: ['restaurantPictures', restaurantId],
    queryFn: () => fetchRestaurantPictures(restaurantId),
    ...config
  });
};

export const useAddRestaurantPictureMutation = ({ onSuccess }: { onSuccess?: (response: GetRestaurantPicturesResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<GetRestaurantPicturesResponse, ErrorResponse, AddRestaurantPictureParams>((params) => addRestaurantPicture(params), {
    onSuccess: (data) => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['restaurantPictures'] });
        onSuccess(data);
      }
    }
  });
};

export const useDeleteRestaurantPictureMutation = ({ onSuccess }: { onSuccess?: (response: UpdateRestaurantResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<UpdateRestaurantResponse, ErrorResponse, RestaurantPictureParams>((params) => deleteRestaurantPictureById(params), {
    onSuccess: (data) => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['restaurantPictures'] });
        onSuccess(data);
      }
    }
  });
};

export const useUpdateRestaurantPictureMutation = ({ onSuccess }: { onSuccess?: (response: UpdateRestaurantResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<UpdateRestaurantResponse, ErrorResponse, UpdateRestaurantPictureParams>((params) => updateRestaurantPictureById(params), {
    onSuccess: (data) => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['restaurantPictures'] });
        onSuccess(data);
      }
    }
  });
};

export const useApproveInactifRestaurantMutation = ({ onSuccess }: { onSuccess?: (response: UpdateRestaurantResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<UpdateRestaurantResponse, ErrorResponse, { restaurantId: string }>(({ restaurantId }) => approveInactiveRestaurantRequest(restaurantId), {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['restaurant', variables.restaurantId]);
      queryClient.invalidateQueries(['restaurantsByEmail']);

      if (onSuccess) {
        onSuccess(data);
      }
    }
  });
};

export const useRejectInactifRestaurantMutation = ({ onSuccess }: { onSuccess?: (response: UpdateRestaurantResponse) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateRestaurantResponse, ErrorResponse, { restaurantId: string; comment: string }>(
    ({ restaurantId, comment }) => rejectInactiveRestaurantRequest(restaurantId, comment),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['restaurant', variables.restaurantId]);
        queryClient.invalidateQueries(['restaurantsByEmail']);

        if (onSuccess) {
          onSuccess(data);
        }
      }
    }
  );
};

export const useFetchAvailableNearbyCitiesQuery = (restaurantId: string) => {
  return useQuery<CityStripeStatus[], ErrorResponse>({
    queryKey: ['availableNearbyCities', restaurantId],
    queryFn: () => fetchAvailableNearbyCitiesRequest(restaurantId)
  });
};
